import React, { useRef, useState } from "react";

import { FailureSeverity, ValidationResult } from "../../utils";
import { Text } from "../items/Text";
import { Stack, StackProps } from "../layouts/stack/Stack";

interface DropZoneProps {
    onDrop(file: File): void;
    onDropFileError?(validationResult: ValidationResult): void;
    disabled: boolean;
    style?: StackProps["style"];
    accept?: string;
    maxFileSizeInMegabytes?: number;
}
export const Dropzone = ({
    onDrop,
    onDropFileError,
    disabled,
    style,
    accept,
    maxFileSizeInMegabytes
}: DropZoneProps): JSX.Element => {
    const [isDragOver, setIsDragOver] = useState<boolean>(false);

    const dropRef = useRef<HTMLInputElement>(null);

    const validateFileMetadata = (file?: File | null): ValidationResult => {
        const validationResult: ValidationResult = { isValid: true, failures: [] };

        const fileFormat = file?.name.split(".").pop()?.toLowerCase();
        const fileSize = file?.size || 0;

        if (accept && file) {
            const acceptedFormats = accept.split(",").map((format) => format.trim().toLowerCase().replace(".", ""));

            if (!fileFormat || !acceptedFormats.includes(fileFormat)) {
                validationResult.isValid = false;
                validationResult.failures.push({
                    code: "InvalidFileFormat",
                    severity: FailureSeverity.Error,
                    message: `Invalid file format. Accepted formats are: ${acceptedFormats.join(", ")}`
                });
            }
        }

        if (maxFileSizeInMegabytes && file) {
            // MB to bytes conversion
            const maxFileSizeInBytes = maxFileSizeInMegabytes * 1000 * 1000;
            if (fileSize > maxFileSizeInBytes) {
                validationResult.isValid = false;
                validationResult.failures.push({
                    code: "FileTooLarge",
                    severity: FailureSeverity.Error,
                    message: `File size exceeds the maximum limit of ${maxFileSizeInMegabytes} MB`
                });
            }
        }

        return validationResult;
    };

    const handleOnChange = (files: FileList | null) => {
        const selectedFile = files?.item(0);

        const validationResult = validateFileMetadata(selectedFile);

        if (!validationResult.isValid && onDropFileError) {
            onDropFileError(validationResult);
            return;
        }

        if (selectedFile && !disabled) {
            onDrop(selectedFile);
        }
    };

    const handleOnDragOver = (e: React.DragEvent<HTMLElement>) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleOnDragLeave = (e: React.DragEvent<HTMLElement>) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleOnClick = () => {
        if (!disabled) {
            dropRef.current?.click();
        }
    };

    const handleOnDrop = (e: React.DragEvent<HTMLElement>) => {
        e.preventDefault();
        setIsDragOver(false);
        handleOnChange(e.dataTransfer.files);
    };

    return (
        <Stack
            onClick={handleOnClick}
            onDrop={handleOnDrop}
            onDragOver={handleOnDragOver}
            onDragLeave={handleOnDragLeave}
            style={{
                minHeight: 250,
                justifyContent: "center",
                alignItems: "center",
                borderStyle: "dashed",
                borderWidth: !disabled && isDragOver ? 3 : 2,
                borderColor: disabled ? "gray" : "black",
                cursor: "pointer",
                backgroundColor: disabled ? "#E3E3E3" : "white",
                ...style
            }}
        >
            <Text styles={{ root: { color: disabled ? "gray" : "black", whiteSpace: "no-wrap", padding: 3 } }}>
                Drag and drop file here, or click to select file
            </Text>
            <input
                style={{ display: "none" }}
                type="file"
                ref={dropRef}
                accept={accept}
                onChange={(e) => {
                    handleOnChange(e.target.files);
                    (e.target as HTMLInputElement).value = "";
                }}
            />
        </Stack>
    );
};
